import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@wondermaps/ui";

interface DeadZonePOI {
  poiId: string;
  poiName: string;
  mapName: string;
  interactions: number;
}

export function DeadZones({
  data,
  threshold,
}: {
  data: DeadZonePOI[];
  threshold: number;
}) {
  // Group by map
  const byMap = new Map<string, DeadZonePOI[]>();
  for (const poi of data) {
    const list = byMap.get(poi.mapName) ?? [];
    list.push(poi);
    byMap.set(poi.mapName, list);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Dead Zone Analysis
          <Badge variant="outline" className="ml-2">
            &lt; {threshold} interactions
          </Badge>
        </CardTitle>
        <CardDescription>
          POIs with fewer total interactions than the threshold. These are
          candidates for better signage, repositioning on the map, improved
          descriptions/photos, or removal if they no longer exist.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {data.length === 0 ? (
          <p className="text-muted-foreground">
            All POIs exceeded the interaction threshold. No dead zones detected.
          </p>
        ) : (
          Array.from(byMap.entries()).map(([mapName, pois]) => (
            <div key={mapName}>
              <h4 className="text-sm font-medium mb-2">{mapName}</h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>POI</TableHead>
                    <TableHead className="text-right">Interactions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pois.map((poi) => (
                    <TableRow key={poi.poiId}>
                      <TableCell>{poi.poiName}</TableCell>
                      <TableCell className="text-right">
                        {poi.interactions === 0 ? (
                          <Badge variant="destructive">0</Badge>
                        ) : (
                          poi.interactions.toLocaleString()
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))
        )}
        <p className="text-xs text-muted-foreground">
          {data.length} POI{data.length !== 1 ? "s" : ""} below threshold
        </p>
      </CardContent>
    </Card>
  );
}
